import { nanoid } from "@reduxjs/toolkit";
import { randomElement } from ".";
import {
  Entity,
  Insight,
  Observation,
  Task,
  Visibility,
  Visualisation,
} from "../types/data";
import { ENTITIES } from "./data";

const texts = [
  "Dan took the deep dive down the rabbit hole.",
  "The elephant didn't want to talk about the person in the room.",
  "She did not cheat on the test, for it was not the right thing to do.",
  "Patricia found the meaning of life in a bowl of Cheerios.",
  "Sometimes it is better to just walk away from things and go back to them later when you’re in a better frame of mind.",
  "As he entered the church he could hear the soft voice of someone whispering into a cell phone.",
  "She says she has the ability to hear the soundtrack of your life.",
  "Jerry liked to look at paintings while eating garlic ice cream.",
  "He was so preoccupied with whether or not he could that he failed to stop to consider if he should.",
  "She always had an interesting perspective on why the world must be flat.",
  "There's an art to getting your way, and spitting olive pits across the table isn't it.",
  "He found a leprechaun in his walnut shell.",
  "The two walked down the slot canyon oblivious to the sound of thunder in the distance.",
  "People who insist on picking their teeth with their elbows are so annoying!",
  "Grape jelly was leaking out the hole in the roof.",
  "Jason didn’t understand why his parents wouldn’t let him sell his little sister at the garage sale.",
  "Gary didn't understand why Doug went upstairs to get one dollar bills when he invited him to go cow tipping.",
  "At that moment she realized she had a sixth sense.",
  "They desperately needed another drummer since the current one only knew how to play bongos.",
  "He decided to count all the sand on the beach as a hobby.",
  "The secret code they created made no sense, even to them.",
  "Chocolate covered crickets were his favorite snack.",
  "I checked to make sure that he was still alive.",
  "Two more days and all his problems would be solved.",
  "I used to practice weaving with spaghetti three hours a day but stopped because I didn't want to die alone.",
  "Writing a list of random sentences is harder than I initially thought it would be.",
  "In hopes of finding out the truth, he entered the one-room library.",
  "She discovered van life is difficult with 2 cats and a dog.",
  "He loved eating his bananas in hot dog buns.",
  "For the 216th time, he said he would quit drinking soda after this last Coke.",
  "It isn't true that my mattress is made of cotton candy.",
  "Flash photography is best used in full sunlight.",
  "The rain pelted the windshield as the darkness engulfed us.",
  "Don't step on the broken glass.",
  "It's not often you find a soggy banana on the street.",
  "He strives to keep the best lawn in the neighborhood.",
  "Pair your designer cowboy hat with scuba gear for a memorable occasion.",
  "She wanted a pet platypus but ended up getting a duck and a ferret instead.",
  "Most shark attacks occur about 10 feet from the beach since that's where the people are.",
  "Everybody should read Chaucer to improve their everyday vocabulary.",
];

export const randomDate = (start?: Date, end?: Date) => {
  if (!start) start = new Date(2022, 7);
  if (!end) end = new Date(2022, 8);

  return new Date(
    start.getTime() + Math.random() * (end.getTime() - start.getTime())
  ).getTime();
};

const randInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

export const task = (): Task => {
  let entity: Entity | undefined = randomElement(ENTITIES);
  if (randInt(0, 100) > 50) entity = undefined;

  const text: string = randomElement(texts);
  const threshold = randInt(0, 100);

  return { entity, text, threshold };
};

export const observation = (): Observation => {
  const entity = randomElement(ENTITIES) as Entity;
  const text = randomElement(texts) as string;
  const date = randomDate(new Date(2022, 0, 1), new Date());

  return { entity, text, date };
};

export const visibility = (entity: Entity): Visibility => {
  const numDays = randInt(60, 120);
  let data: number[] = [];

  const a = 0.05;
  const b = 10; //play with these values to your liking
  let y;

  for (let x = 0; x < numDays; x++) {
    //loop here from 0 to whatever
    y = (a * x) ^ (2 + b * x * (randInt(5, 10) / 100));
    data.push(y);
  }

  // Noramlise value
  const normalise = (data: number[], min: number, max: number) => {
    const originalMin = Math.min(...data);
    const originalMax = Math.max(...data);

    let ret: number[] = [];

    for (let i = 0; i < data.length; i++) {
      const value = data[i];
      ret.push(
        ((max - min) / (originalMax - originalMin)) * (value - originalMax) +
          max
      );
    }

    return ret;

    // newvalue= (max'-min')/(max-min)*(value-max)+max'
  };

  data = normalise(data, randInt(0, 20), randInt(80, 100));

  // Randomly, make 50% of the graphs trend downwards
  if (randInt(0, 100) > 50) data = data.map((d) => 100 - d);

  return { entity, data, target: undefined };
};

export const visualisation = (): Visualisation | undefined => {
  // No visualisation 70% of the time
  if (randInt(0, 100) < 60) return;

  if (randInt(0, 100) > 50)
    return { type: "percentage", data: randInt(10, 90) };
  else {
    return {
      type: "bar",
      data: Array.from(Array(randInt(2, 4)).keys()).map((i) => randInt(0, 100)),
    };
  }
};

export const insight = (): Insight => {
  const titles = [
    "You voted remain",
    "You were fired from your job last week",
    "You hate driving",
    "You secretly support Nigel Farage",
    "Rubber fanatic",
    "You believe unicorns are real",
    "If you could, you would only eat McDonalds",
    "Your favourite way to unwind is with a bottle of wine",
  ];

  const title: string = randomElement(titles);

  const visibilities = ENTITIES.map((e) => visibility(e));

  const tasks: Task[] = Array.from(Array(randInt(10, 20)).keys()).map((i) =>
    task()
  );

  const observations: Observation[] = Array.from(
    Array(randInt(3, 20)).keys()
  ).map((i) => observation());

  observations.sort((a, b) => (a.date - b.date > 0 ? -1 : 11));

  const date = randomDate(new Date(2022, 7), new Date(2022, 8));

  return {
    id: nanoid(),
    title,
    visibilities,
    tasks,
    observations,
    visualisation: visualisation(),
    relatedInsights: [],
    date,
  };
};

export default Array.from(Array(10).keys()).map((i) => insight());
